import { createRouter, createWebHistory, type RouteRecordRaw, RouterView } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCheckoutStore } from '@/stores/useCheckoutStore'

const LANGS = ['ru', 'kk', 'en'] as const
type Lang = (typeof LANGS)[number]
const langRe = 'ru|kk|en'

function getStoredLang(): Lang {
  const ls = (localStorage.getItem('lang') || 'ru') as Lang
  return (LANGS as readonly string[]).includes(ls) ? (ls as Lang) : 'ru'
}
function saveLang(l?: string) {
  if (l && (LANGS as readonly string[]).includes(l)) localStorage.setItem('lang', l)
}

const routes: Array<RouteRecordRaw> = [
  { path: '/', redirect: () => `/${getStoredLang()}/` },

  {
    path: `/:lang(${langRe})`,
    component: RouterView,
    children: [
      // PUBLIC
      { path: '', name: 'Home', component: () => import('@/pages/HomePage.vue') },
      { path: 'objects/:id', name: 'ObjectDetail', component: () => import('@/pages/ObjectDetail.vue'), props: true },
      { path: 'movies/:id', name: 'MovieDetail', component: () => import('@/pages/MovieDetail.vue'), props: true },

      // Покупка/оплата — только после логина и успешной резервации
      {
        path: 'payment',
        name: 'Payment',
        component: () => import('@/pages/PaymentPage.vue'),
        meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'], requiresOrder: true },
      },

      // Профиль пользователя (нужно авторизоваться)
      {
        path: 'profile',
        name: 'ProfileLayout',
        meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] },
        redirect: { name: 'ProfileSettings' },
        component: () => import('@/layouts/ProfileLayout.vue'),
        children: [
          { path: 'settings', name: 'ProfileSettings', component: () => import('@/pages/profile/ProfileSettings.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
          { path: 'tickets', name: 'MyTickets', component: () => import('@/pages/profile/MyTickets.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
          { path: 'bank-cards', name: 'BankCards', component: () => import('@/pages/profile/BankCards.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
          { path: 'pending-orders', name: 'ProfilePendingOrders', component: () => import('@/pages/profile/ProfilePendingOrders.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
          { path: 'reviews', name: 'ProfileReviews', component: () => import('@/pages/profile/ProfileReviews.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
          { path: 'favorites', name: 'ProfileFavorites', component: () => import('@/pages/profile/ProfileFavorites.vue'), meta: { requiresAuth: true, roles: ['user', 'admin', 'controller'] } },
        ],
      },

      // Админка (нужно авторизоваться)
      {
        path: 'secure-admin',
        name: 'Admin',
        meta: { requiresAuth: true, roles: ['admin'] },
        redirect: { name: 'AdminDashboard' },
        component: () => import('@/layouts/admin/AdminLayout.vue'),
        children: [
          { path: 'dashboard', name: 'AdminDashboard', component: () => import('@/pages/admin/MainDashboard.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'objects', name: 'AdminObjects', component: () => import('@/pages/admin/AdminObjects.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'orders', name: 'AdminOrders', component: () => import('@/pages/admin/AdminOrders.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'details/:id', name: 'AdminDetails', component: () => import('@/pages/admin/AdminDetails.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'exports', name: 'AdminObjectExports', component: () => import('@/pages/admin/AdminObjectExports.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'sales-of-objects', name: 'AdminSalesOfObjects', component: () => import('@/pages/admin/AdminSalesOfObjects.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'unique-visitors', name: 'AdminUniqueVisitors', component: () => import('@/pages/admin/AdminUniqueVisitors.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'tickets-objects', name: 'AdminTicketsOfObjects', component: () => import('@/pages/admin/AdminTicketsOfObjects.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
          { path: 'sales-marketing', name: 'AdminSalesMarketing', component: () => import('@/pages/admin/AdminSalesMarketing.vue'), meta: { requiresAuth: true, roles: ['admin'] } },
        ],
      },

      // Контроллер (нужно авторизоваться)
      { path: 'controller', name: 'ControllerDashboard', component: () => import('@/pages/controller/MainController.vue'), meta: { requiresAuth: true, roles: ['controller'] } },

      // Аутентификация (public)
      { path: 'register', name: 'Register', component: () => import('@/pages/auth/RegisterPage.vue') },
      { path: 'login', name: 'Login', component: () => import('@/pages/auth/LoginPage.vue') },
      { path: 'logout', name: 'Logout', component: () => import('@/pages/auth/LoginPage.vue') },
      { path: 'verify-email', name: 'VerifyEmail', component: () => import('@/pages/auth/VerifyEmailPage.vue') },
      { path: 'verify-phone', name: 'VerifyPhone', component: () => import('@/pages/auth/VerifyPhonePage.vue') },

      { path: 'access-denied', name: 'AccessDenied', component: () => import('@/pages/AccessDenied.vue') },
      { path: ':pathMatch(.*)*', name: 'NotFound', component: () => import('@/pages/NotFound.vue') },
    ],
  },

  // редиректы без языка
  { path: '/objects/:id', redirect: (to: any) => `/${getStoredLang()}/objects/${to.params.id}` },
  { path: '/login', redirect: () => `/${getStoredLang()}/login` },
  { path: '/register', redirect: () => `/${getStoredLang()}/register` },
]


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ left: 0, top: 0 }),
})

router.beforeEach(async (to: any, _from: any, next: any) => {
  const lang = to.params.lang as string | undefined

  if (lang && (LANGS as readonly string[]).includes(lang)) {
    saveLang(lang)
  } else if (to.path === '/' || !lang) {
    const target = getStoredLang()
    return next({
      path: `/${target}${to.fullPath === '/' ? '/' : to.fullPath}`,
      replace: true,
    })
  }

  const auth = useAuthStore()
  const checkout = useCheckoutStore()

  const requiresAuth = Boolean(to.meta.requiresAuth)
  const allowedRoles = (to.meta.roles as string[] | undefined) ?? []
  const requiresOrder = Boolean(to.meta.requiresOrder)

  if (!requiresAuth && allowedRoles.length === 0 && !requiresOrder) {
    if (!auth.isAuthChecked) {
      await auth.checkAuth()
    }
    return next()
  }

  const ok = await auth.checkAuth()
  if (!ok) {
    const targetLang = (to.params.lang as string) || getStoredLang()
    return next({
      name: 'Login',
      params: { lang: targetLang },
      query: { redirect: to.fullPath },
    })
  }

  if (allowedRoles.length > 0) {
    const role = auth.profile?.role as string | undefined
    if (!role || !allowedRoles.includes(role)) {
      const targetLang = (to.params.lang as string) || getStoredLang()
      return next({
        name: 'AccessDenied',
        params: { lang: targetLang },
      })
    }
  }

  if (requiresOrder) {
    const q = to.query.order_id
    const orderIdFromQuery =
      typeof q === 'string' ? q : Array.isArray(q) ? q[0] : undefined

    if (orderIdFromQuery) return next()

    if (checkout.orderId) {
      const targetLang = (to.params.lang as string) || getStoredLang()
      return next({
        name: 'Payment',
        params: { lang: targetLang },
        query: { ...to.query, order_id: checkout.orderId },
        replace: true,
      })
    }

    const targetLang = (to.params.lang as string) || getStoredLang()
    return next({ name: 'Home', params: { lang: targetLang } })
  }

  return next()
})

export default router

export function withLang(name: string, params: Record<string, unknown> = {}, query?: Record<string, unknown>) {
  const l = getStoredLang()
  return { name, params: { lang: l, ...params }, query }
}
